import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Container,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Divider,
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiEye, FiEyeOff, FiMail, FiLock } from "react-icons/fi";
import { useSendOTP, useResendOTP, useVerifyOTP, useSetNewPassword } from "../../services/authService";
import { useNavigate, useLocation } from "react-router-dom";
import RateLimitError from "../../components/RateLimitError";
import { isRateLimitError } from "../../utils/rateLimit";

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  
  const [step, setStep] = useState("email"); // "email", "otp", "password"
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [sendOTPRateLimit, setSendOTPRateLimit] = useState(null);
  const [resendOTPRateLimit, setResendOTPRateLimit] = useState(null);
  const [verifyOTPRateLimit, setVerifyOTPRateLimit] = useState(null);
  
  const inputRefs = useRef([]);

  const handleSendOTPError = (error) => {
    if (isRateLimitError(error)) {
      const { ErrorHandler } = require("../../services/errorHandler");
      const rateLimitInfo = ErrorHandler(error);
      if (rateLimitInfo?.isRateLimited) {
        setSendOTPRateLimit({
          message: rateLimitInfo.message,
          retryAfter: rateLimitInfo.retryAfter,
        });
      }
    } else {
      setSendOTPRateLimit(null);
    }
  };

  const handleResendOTPError = (error) => {
    if (isRateLimitError(error)) {
      const { ErrorHandler } = require("../../services/errorHandler");
      const rateLimitInfo = ErrorHandler(error);
      if (rateLimitInfo?.isRateLimited) {
        setResendOTPRateLimit({
          message: rateLimitInfo.message,
          retryAfter: rateLimitInfo.retryAfter,
        });
      }
    } else {
      setResendOTPRateLimit(null);
    }
  };

  const handleVerifyOTPError = (error) => {
    if (isRateLimitError(error)) {
      const { ErrorHandler } = require("../../services/errorHandler");
      const rateLimitInfo = ErrorHandler(error);
      if (rateLimitInfo?.isRateLimited) {
        setVerifyOTPRateLimit({
          message: rateLimitInfo.message,
          retryAfter: rateLimitInfo.retryAfter,
        });
      }
    } else {
      setVerifyOTPRateLimit(null);
    }
  };

  const { sendOTPPayload, sendOTPIsLoading } = useSendOTP(() => {
    setSendOTPRateLimit(null); // Clear rate limit on success
    setStep("otp");
    startResendCountdown();
  }, handleSendOTPError);

  const { resendOTPPayload, resendOTPIsLoading } = useResendOTP(() => {
    setResendOTPRateLimit(null); // Clear rate limit on success
    startResendCountdown();
  }, handleResendOTPError);

  const { verifyOTPPayload, verifyOTPIsLoading } = useVerifyOTP(() => {
    setVerifyOTPRateLimit(null); // Clear rate limit on success
    setStep("password");
  }, handleVerifyOTPError);

  const { setNewPasswordPayload, setNewPasswordIsLoading } = useSetNewPassword(() => {
    navigate("/login");
  });

  // Password strength checker
  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;
    return strength;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError("");

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const startResendCountdown = () => {
    setResendCountdown(60);
    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = (e) => {
    e.preventDefault();
    setEmailError("");

    if (!email) {
      setEmailError("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    sendOTPPayload({ email });
  };

  const handleResendOTP = () => {
    resendOTPPayload({ email });
  };

  const handleVerifyOTP = (e) => {
    e.preventDefault();
    
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP");
      return;
    }

    verifyOTPPayload({ email, otp: otpString });
  };

  const handleSetNewPassword = (e) => {
    e.preventDefault();

    setPasswordError("");
    setConfirmPasswordError("");

    if (!password) {
      setPasswordError("Password is required");
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(password)) {
      setPasswordError("Password must be at least 8 characters with uppercase, lowercase, number, and special character");
      return;
    }

    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your password");
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }

    const payload = {
      email,
      otp: otp.join(""),
      new_password: password,
      confirm_password: confirmPassword,
    };

    setNewPasswordPayload(payload);
  };

  const getStepTitle = () => {
    switch (step) {
      case "email":
        return "Reset Password";
      case "otp":
        return "Verify OTP";
      case "password":
        return "Set New Password";
      default:
        return "Reset Password";
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case "email":
        return "Enter your email address and we'll send you a verification code";
      case "otp":
        return `Enter the 6-digit code sent to ${email}`;
      case "password":
        return "Enter your new password below";
      default:
        return "";
    }
  };

  return (
    <Box
      bg="gray.50"
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <Container maxW="md">
        <Box
          bg={bgColor}
          borderRadius="20px"
          p={10}
          shadow="xl"
          border="1px solid"
          borderColor={borderColor}
          position="relative"
        >
          {/* Back Button */}
          <IconButton
            icon={<FiArrowLeft />}
            variant="ghost"
            aria-label="Go back"
            onClick={() => {
              if (step === "email") {
                navigate("/login");
              } else if (step === "otp") {
                setStep("email");
                setOtp(["", "", "", "", "", ""]);
              } else {
                setStep("otp");
                setPassword("");
                setConfirmPassword("");
              }
            }}
            position="absolute"
            top={6}
            left={6}
            color="gray.600"
            _hover={{ bg: "gray.100" }}
          />

          {/* Header */}
          <VStack spacing={6} mb={8}>
            <Box textAlign="center">
              <Heading size="xl" color="gray.900" fontWeight="700" mb={3}>
                {getStepTitle()}
              </Heading>
              <Text color="gray.600" fontSize="md">
                {getStepDescription()}
              </Text>
            </Box>
          </VStack>

          {/* Email Step */}
          {step === "email" && (
            <form onSubmit={handleSendOTP}>
              <VStack spacing={6}>
                {sendOTPRateLimit && (
                  <RateLimitError
                    message={sendOTPRateLimit.message}
                    retryAfter={sendOTPRateLimit.retryAfter}
                    onComplete={() => setSendOTPRateLimit(null)}
                    width="100%"
                  />
                )}
                
                <InputGroup>
                  <InputLeftElement pointerEvents="none" h="56px" color="gray.400">
                    <FiMail size={20} />
                  </InputLeftElement>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    isInvalid={emailError ? true : false}
                    size="lg"
                    h="56px"
                    fontSize="md"
                    pl="50px"
                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                  />
                </InputGroup>
                {emailError && (
                  <Text color="red.500" fontSize="sm">
                    {emailError}
                  </Text>
                )}

                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  width="full"
                  isLoading={sendOTPIsLoading}
                  loadingText="Sending OTP..."
                  borderRadius="12px"
                  fontWeight="600"
                  h="56px"
                  fontSize="md"
                  isDisabled={!!sendOTPRateLimit}
                  _hover={!sendOTPRateLimit ? { transform: "translateY(-1px)", shadow: "lg" } : {}}
                  transition="all 0.2s"
                >
                  {sendOTPRateLimit 
                    ? `Try again in ${Math.ceil(sendOTPRateLimit.retryAfter / 60)} min` 
                    : "Send OTP"}
                </Button>
              </VStack>
            </form>
          )}

          {/* OTP Step */}
          {step === "otp" && (
            <form onSubmit={handleVerifyOTP}>
              <VStack spacing={6}>
                {verifyOTPRateLimit && (
                  <RateLimitError
                    message={verifyOTPRateLimit.message}
                    retryAfter={verifyOTPRateLimit.retryAfter}
                    onComplete={() => setVerifyOTPRateLimit(null)}
                    width="100%"
                  />
                )}
                
                {resendOTPRateLimit && (
                  <RateLimitError
                    message={resendOTPRateLimit.message}
                    retryAfter={resendOTPRateLimit.retryAfter}
                    onComplete={() => setResendOTPRateLimit(null)}
                    width="100%"
                  />
                )}
                
                <Alert status="info" borderRadius="12px">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Check Your Email</AlertTitle>
                    <AlertDescription>
                      We've sent a 6-digit verification code to {email}
                    </AlertDescription>
                  </Box>
                </Alert>

                <Box>
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    Enter 6-digit OTP
                  </Text>
                  <HStack spacing={2} justify="center">
                    {otp.map((digit, index) => (
                      <Input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        size="lg"
                        width="50px"
                        textAlign="center"
                        fontSize="xl"
                        fontWeight="bold"
                        borderColor={otpError ? "red.300" : "gray.300"}
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                        maxLength={1}
                        pattern="[0-9]*"
                        inputMode="numeric"
                      />
                    ))}
                  </HStack>
                  {otpError && (
                    <Text color="red.500" fontSize="sm" mt={2}>
                      {otpError}
                    </Text>
                  )}
                </Box>

                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  width="full"
                  isLoading={verifyOTPIsLoading}
                  loadingText="Verifying OTP..."
                  borderRadius="12px"
                  fontWeight="600"
                  h="56px"
                  fontSize="md"
                  isDisabled={!!verifyOTPRateLimit}
                  _hover={!verifyOTPRateLimit ? { transform: "translateY(-1px)", shadow: "lg" } : {}}
                  transition="all 0.2s"
                >
                  {verifyOTPRateLimit 
                    ? `Try again in ${Math.ceil(verifyOTPRateLimit.retryAfter / 60)} min` 
                    : "Verify OTP"}
                </Button>

                <Divider />

                <VStack spacing={3}>
                  <Text fontSize="sm" color="gray.600">
                    Didn't receive the code?
                  </Text>
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={handleResendOTP}
                    isLoading={resendOTPIsLoading}
                    isDisabled={resendCountdown > 0 || !!resendOTPRateLimit}
                    color="blue.500"
                    _hover={{ bg: "blue.50" }}
                  >
                    {resendOTPRateLimit
                      ? `Try again in ${Math.ceil(resendOTPRateLimit.retryAfter / 60)} min`
                      : resendCountdown > 0 
                        ? `Resend in ${resendCountdown}s` 
                        : "Resend OTP"
                    }
                  </Button>
                </VStack>
              </VStack>
            </form>
          )}

          {/* Password Step */}
          {step === "password" && (
            <form onSubmit={handleSetNewPassword}>
              <VStack spacing={6}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" h="56px" color="gray.400">
                    <FiLock size={20} />
                  </InputLeftElement>
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={password}
                    onChange={handlePasswordChange}
                    isInvalid={passwordError ? true : false}
                    size="lg"
                    h="56px"
                    fontSize="md"
                    pl="50px"
                    pr="50px"
                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                  />
                  <InputRightElement h="56px">
                    <IconButton
                      icon={showPassword ? <FiEyeOff /> : <FiEye />}
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      color="gray.500"
                      _hover={{ bg: "gray.100" }}
                    />
                  </InputRightElement>
                </InputGroup>
                {passwordError && (
                  <Text color="red.500" fontSize="sm">
                    {passwordError}
                  </Text>
                )}

                {/* Password Strength Indicator */}
                {password && (
                  <Box width="full">
                    <Text fontSize="xs" color="gray.600" mb={2}>
                      Password Strength: {passwordStrength < 3 ? "Weak" : passwordStrength < 5 ? "Good" : "Strong"}
                    </Text>
                    <HStack spacing={1}>
                      {[1, 2, 3, 4, 5].map((level) => (
                        <Box
                          key={level}
                          h="4px"
                          flex={1}
                          bg={level <= passwordStrength ? 
                            (passwordStrength < 3 ? "red.400" : passwordStrength < 5 ? "yellow.400" : "green.400") : 
                            "gray.200"
                          }
                          borderRadius="2px"
                        />
                      ))}
                    </HStack>
                  </Box>
                )}

                <InputGroup>
                  <InputLeftElement pointerEvents="none" h="56px" color="gray.400">
                    <FiLock size={20} />
                  </InputLeftElement>
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    isInvalid={confirmPasswordError ? true : false}
                    size="lg"
                    h="56px"
                    fontSize="md"
                    pl="50px"
                    pr="50px"
                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                  />
                  <InputRightElement h="56px">
                    <IconButton
                      icon={showPassword ? <FiEyeOff /> : <FiEye />}
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      color="gray.500"
                      _hover={{ bg: "gray.100" }}
                    />
                  </InputRightElement>
                </InputGroup>
                {confirmPasswordError && (
                  <Text color="red.500" fontSize="sm">
                    {confirmPasswordError}
                  </Text>
                )}

                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  width="full"
                  isLoading={setNewPasswordIsLoading}
                  loadingText="Setting new password..."
                  borderRadius="12px"
                  fontWeight="600"
                  h="56px"
                  fontSize="md"
                  _hover={{ transform: "translateY(-1px)", shadow: "lg" }}
                  transition="all 0.2s"
                >
                  Reset Password
                </Button>
              </VStack>
            </form>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPassword; 